"use client";

import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { RefreshCcw, Search, Tag, Truck } from "lucide-react";

import NavPillMenu from "@/src/app/components/nav-pill-menu";
import HeaderActionIcons from "@/src/components/layout/HeaderActionIcons";
import { siteConfig } from "@/src/config/site";

export default function Navbar() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentSearchQuery =
    pathname === "/products" ? searchParams.get("q") ?? "" : "";
  const [searchQuery, setSearchQuery] = useState(currentSearchQuery);

  useEffect(() => {
    setSearchQuery(currentSearchQuery);
  }, [currentSearchQuery]);

  const buildProductsHref = (query: string) => {
    const nextParams =
      pathname === "/products"
        ? new URLSearchParams(searchParams.toString())
        : new URLSearchParams();
    const trimmedQuery = query.trim().replace(/\s+/g, " ");

    if (trimmedQuery) {
      nextParams.set("q", trimmedQuery);
    } else {
      nextParams.delete("q");
    }

    const nextQuery = nextParams.toString();

    return nextQuery ? "/products?" + nextQuery : "/products";
  };

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    router.push(buildProductsHref(searchQuery));
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    if (pathname === "/products" && currentSearchQuery && value.trim() === "") {
      router.replace(buildProductsHref(""));
    }
  };

  return (
    <header
      className="relative z-[120] hidden w-full bg-white pt-16 lg:block"
      data-reveal="down"
      dir="rtl"
    >
      <div className="fixed left-0 right-0 top-0 z-[140] border-b border-[rgba(241,89,34,0.1)] bg-white">
        <div className="mx-auto flex min-h-16 w-full max-w-[1400px] items-center justify-center gap-0 px-6 lg:px-10">
          <div className="flex items-center gap-3 px-6 text-[0.95rem] font-semibold text-[var(--color-ink)]">
            <Truck className="h-5 w-5 text-[var(--color-accent)]" />
            <span>طلبك يوصلك أسرع مما تتوقع</span>
          </div>
          <span className="h-7 w-px bg-[rgba(241,89,34,0.18)]" aria-hidden="true" />
          <div className="flex items-center gap-3 px-6 text-[0.95rem] font-semibold text-[var(--color-ink)]">
            <Tag className="h-5 w-5 text-[var(--color-accent)]" />
            <span>تخفيضات الربيع حتى 40%</span>
          </div>
          <span className="h-7 w-px bg-[rgba(241,89,34,0.18)]" aria-hidden="true" />
          <div className="flex items-center gap-3 px-6 text-[0.95rem] font-semibold text-[var(--color-ink)]">
            <RefreshCcw className="h-5 w-5 text-[var(--color-accent)]" />
            <span>ضمان وخدمة ما بعد البيع </span>
          </div>
        </div>
      </div>

      <div className="w-full bg-white">
        <div className="mx-auto flex min-h-[108px] w-full items-center justify-between gap-8 px-6 lg:px-10">
          <div className="flex shrink-0 items-center gap-4">
            <form
              action="/products"
              method="get"
              onSubmit={handleSearchSubmit}
              className="flex h-12 w-[320px] items-center gap-3 rounded border border-[#ee376a] bg-white px-4"
            >
              <Search className="h-5 w-5 text-[var(--color-accent)]" />
              <input
                type="search"
                name="q"
                value={searchQuery}
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder="ابحث عن المنتجات..."
                className="w-full bg-transparent text-sm font-medium text-[var(--color-ink)] outline-none placeholder:text-[var(--color-accent)]"
                aria-label="ابحث عن المنتجات"
              />
            </form>
            <HeaderActionIcons />
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-center">
            <NavPillMenu
              items={siteConfig.navigation}
              ariaLabel="روابط المتجر"
              variant="default"
            />
          </div>

          <Link href="/" className="flex shrink-0 items-center gap-3">
            <div className="relative h-[60px] w-[150px] lg:h-[72px] lg:w-[190px]">
              <Image
                src="/Office%20Furniture/image-removebg-preview.png"
                alt={`شعار ${siteConfig.name}`}
                fill
                priority
                sizes="(min-width: 1024px) 190px, 150px"
                quality={60}
                className="object-contain object-right"
              />
            </div>
          </Link>
        </div>
      </div>
    </header>
  );
}